import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/useLang";
import { getAppStrings } from "../i18n/appI18n";

const fmt = (s, vars) => Object.keys(vars || {}).reduce((acc, k) => acc.replace(`{${k}}`, vars[k]), s);

export default function VerifyEmail() {
  const { user, logout, refresh } = useAuth();
  const lang = useLang();
  const t = getAppStrings(lang).verify;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const sentOnce = useRef(false);

  useEffect(() => {
    // fire an initial code on mount (backend already sent one on register; this is a safety re-send only if user lands here fresh)
    if (!sentOnce.current) { sentOnce.current = true; setCooldown(45); }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return;
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { code: code.trim() });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/auth/resend-otp");
      toast.success(t.resent);
      setCooldown(45);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not resend the code.";
      toast.error(detail);
    }
  };

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body flex items-center justify-center px-5" data-testid="verify-email-page">
      <div className="w-full max-w-sm">
        <div className="block text-center mb-8">
          <span className="font-heading text-3xl text-reveal-rose">Reveal</span>
        </div>
        <div className="bg-reveal-burgundy border border-reveal-rose/20 rounded-3xl px-8 py-9">
          <div className="w-14 h-14 rounded-full bg-reveal-rose/15 flex items-center justify-center mb-5">
            <MailCheck className="w-7 h-7 text-reveal-rose" />
          </div>
          <h1 className="font-heading text-3xl text-reveal-cream mb-2">{t.title}</h1>
          <p className="text-sm text-reveal-cream/60 mb-7 leading-relaxed">
            {fmt(t.subtitle, { email: user?.email || "" })}
          </p>

          <form onSubmit={submit}>
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder={t.placeholder}
              data-testid="otp-code-input"
              className="w-full bg-black/40 border border-reveal-rose/40 rounded-xl px-4 py-3 mb-5 text-center text-2xl tracking-[0.4em] text-reveal-cream placeholder:text-reveal-cream/30 placeholder:tracking-normal placeholder:text-base outline-none focus:border-reveal-rose transition-colors"
            />
            <button type="submit" disabled={loading || code.length !== 6} data-testid="otp-verify-button"
              className="w-full bg-reveal-rose text-black font-medium py-3 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-50 mb-4">
              {loading ? t.verifying : t.verify}
            </button>
          </form>

          <button onClick={resend} disabled={cooldown > 0} data-testid="otp-resend-button"
            className="w-full text-sm text-reveal-rose hover:text-reveal-cream transition-colors disabled:opacity-40 mb-2">
            {cooldown > 0 ? fmt(t.resendIn, { s: cooldown }) : t.resend}
          </button>
          <button onClick={logout} data-testid="verify-logout-button"
            className="w-full text-xs text-reveal-cream/40 hover:text-reveal-cream/70 transition-colors">
            {t.logout}
          </button>
        </div>
      </div>
    </div>
  );
}
